import {none, option, Option} from "ts-option";
import * as moment from "moment";
import {CreateUpdatePartnerBrandApiDto, PartnerBrandApiDto} from "../../api/model/rest";
import JsonConverter from "../../api/helper/JsonConverter";
import AuthenticatedEntity from "../user/AuthenticatedEntity";
import {DealerPrice, VehicleCategory} from "../brand/DealerPrice";
import {CreditValueCapMode} from "./CreditValueCapMode";

/**
 * Partner brand model.
 */
export enum PartnerBrandCurrency {
	EUR = "EUR",
	GBP = "GBP"
}

export default class PartnerBrand {
	constructor(
		public readonly id: string,
		public readonly brandId: string,
		public readonly name: string,
		public readonly tradingName: Option<string>,
		public readonly labourCost: number,
		public readonly markup: number,
		public readonly domain: Option<string>,
		public readonly phoneNumber: string,
		public readonly logoId: Option<string>,
		public readonly siteId: Option<string>,
		public readonly vatRegistrationNumber: Option<string>,
		public readonly freeText: Option<string>,
		public readonly gasNumber: Option<string>,
		public readonly email: Option<string>,
		public readonly quoteEmailStyleSuffix: string,
		public readonly vatRegistered: boolean,
		public readonly bdpId: Option<string>,
		public readonly encourageSms: boolean,
		public readonly accountManagerId: Option<string>,
		public readonly noteImportant: Option<string>,
		public readonly noteImportantUpdatedDateTime: Option<moment.Moment>,
		public readonly noteImportantAuthor: Option<AuthenticatedEntity>,
		public readonly billDueDays: number,
		public readonly invoiceDueDays: number,
		public readonly remittanceNotice: boolean,
		public readonly preferredMerchantId: Option<string>,
		public readonly leadUserId: Option<string>,
		public readonly zendeskSellCompanyCode: Option<string>,
		public readonly priceAdjustmentFactor: number,
		public readonly preferredMerchantBranchLocationId: Option<string>,
		public readonly automaticPayment: boolean,
		public readonly bankAccountNumber: Option<string>,
		public readonly bankSortCode: Option<string>,
		public readonly accountHolder: Option<string>,
		public readonly showRemittanceOnMobile: boolean,
		public readonly gasCertAvailable: boolean,
		public readonly serviceReminderAvailable: boolean,
		public readonly defaultUtilityId: string,
		public readonly dealerPrices: DealerPrice[],
		public readonly creditValueAirToWaterHp: Option<number>,
		public readonly creditValueAirToAirHp: Option<number>,
		public readonly creditValueWaterToWaterHp: Option<number>,
		public readonly creditValueBrineToWaterHp: Option<number>,
		public readonly creditValueFlowsave: Option<number>,
		public readonly currency: PartnerBrandCurrency,
		/**
		 * How this partner departs from the brand's cap on mileage payouts.
		 */
		public readonly creditValueCapMode: CreditValueCapMode,
		/**
		 * This partner's own cap in cents, set only alongside {@link CreditValueCapMode.FIXED}.
		 */
		public readonly creditValueCap: Option<number>,
		public readonly fleetFormId: Option<string> = none,
		public readonly hasSignature: boolean = false
		) {}

	static fromJson(json: PartnerBrandApiDto): PartnerBrand {
		return new PartnerBrand(
			json.id,
			json.brandId,
			json.name,
			option(json.tradingName),
			json.labourCost,
			json.markup,
			option(json.domain),
			json.phoneNumber,
			option(json.logoId),
			option(json.siteId),
			option(json.vatRegistrationNumber),
			option(json.freeText),
			option(json.gasNumber),
			option(json.email),
			json.quoteEmailStyleSuffix,
			json.vatRegistered,
			option(json.bdpId),
			json.encourageSms,
			option(json.accountManagerId),
			option(json.noteImportant),
			json.noteImportantUpdatedDateTime ? option(moment(json.noteImportantUpdatedDateTime)) : none,
			json.noteImportantAuthor ? option(AuthenticatedEntity.fromJson(json.noteImportantAuthor)) : none,
			json.billDueDays,
			json.invoiceDueDays,
			json.remittanceNotice,
			option(json.preferredMerchantId),
			option(json.leadUserId),
			option(json.zendeskSellCompanyCode),
			json.priceAdjustmentFactor,
			option(json.preferredMerchantBranchLocationId),
			json.automaticPayment,
			none, //
			none, // fields used only to update bank data if needed. different object is used to fetch it due to security reasons.
			none,  //
			json.showRemittanceOnMobile,
			json.gasCertAvailable,
			json.serviceReminderAvailable,
			json.defaultUtilityId,
			(json.dealerPrices || []).map(p => ({
				vehicleCategory: p.vehicleCategory as VehicleCategory,
				registrationYear: p.registrationYear !== undefined ? p.registrationYear : null,
				pricePennies: p.pricePennies
			})),
			option(json.creditValueAirToWaterHp),
			option(json.creditValueAirToAirHp),
			option(json.creditValueWaterToWaterHp),
			option(json.creditValueBrineToWaterHp),
			option(json.creditValueFlowsave),
			json.currency === PartnerBrandCurrency.GBP ? PartnerBrandCurrency.GBP : PartnerBrandCurrency.EUR,
			json.creditValueCapMode,
			option(json.creditValueCap),
			option(json.fleetFormId),
			json.hasSignature === true
		)
	}

	toJson(): CreateUpdatePartnerBrandApiDto {
		const json: CreateUpdatePartnerBrandApiDto = {
			brandId: this.brandId,
			name: this.name,
			tradingName: this.tradingName.orNull,
			labourCost: this.labourCost,
			markup: this.markup,
			domain: this.domain.orNull,
			phoneNumber: this.phoneNumber,
			logoId: this.logoId.orNull,
			siteId: this.siteId.orNull,
			vatRegistrationNumber: this.vatRegistrationNumber.orNull,
			freeText: this.freeText.orNull,
			gasNumber: this.gasNumber.orNull,
			email: this.email.orNull,
			quoteEmailStyleSuffix: this.quoteEmailStyleSuffix,
			vatRegistered: this.vatRegistered,
			bdpId: this.bdpId.orNull,
			encourageSms: this.encourageSms,
			accountManagerId: this.accountManagerId.orNull,
			noteImportant: this.noteImportant.orNull,
			billDueDays: this.billDueDays,
			invoiceDueDays: this.invoiceDueDays,
			remittanceNotice: this.remittanceNotice,
			preferredMerchantId: this.preferredMerchantId.orNull,
			leadUserId: this.leadUserId.orNull,
			zendeskSellCompanyCode: this.zendeskSellCompanyCode.orNull,
			priceAdjustmentFactor: this.priceAdjustmentFactor,
			preferredMerchantBranchLocationId: this.preferredMerchantBranchLocationId.orNull,
			automaticPayment: this.automaticPayment,
			bankAccountNumber: this.bankAccountNumber.orNull,
			bankSortCode: this.bankSortCode.orNull,
			accountHolder: this.accountHolder.orNull,
			showRemittanceOnMobile: this.showRemittanceOnMobile,
			gasCertAvailable: this.gasCertAvailable,
			serviceReminderAvailable: this.serviceReminderAvailable,
			defaultUtilityId: this.defaultUtilityId,
			dealerPrices: this.dealerPrices.map(p => ({
				vehicleCategory: p.vehicleCategory,
				registrationYear: p.registrationYear !== null ? p.registrationYear : undefined,
				pricePennies: p.pricePennies
			})),
			creditValueAirToWaterHp: this.creditValueAirToWaterHp.orNull,
			creditValueAirToAirHp: this.creditValueAirToAirHp.orNull,
			creditValueWaterToWaterHp: this.creditValueWaterToWaterHp.orNull,
			creditValueBrineToWaterHp: this.creditValueBrineToWaterHp.orNull,
			creditValueFlowsave: this.creditValueFlowsave.orNull,
			currency: this.currency,
			creditValueCapMode: this.creditValueCapMode,
			// Sent only for a fixed cap. For the other two modes there is no amount to send, and
			// removeEmptyFields below drops the key rather than sending a null the backend would ignore.
			creditValueCap: this.creditValueCapMode === CreditValueCapMode.FIXED ? this.creditValueCap.orNull : null
		};
		return JsonConverter.removeEmptyFields(json);
	}

	copy({
		id = this.id,
		brandId = this.brandId,
		name = this.name,
		tradingName = this.tradingName,
		labourCost = this.labourCost,
		markup = this.markup,
		domain = this.domain,
		phoneNumber = this.phoneNumber,
		logoId = this.logoId,
		siteId = this.siteId,
		vatRegistrationNumber = this.vatRegistrationNumber,
		freeText = this.freeText,
		gasNumber = this.gasNumber,
		email = this.email,
		quoteEmailStyleSuffix = this.quoteEmailStyleSuffix,
		vatRegistered = this.vatRegistered,
		bdpId = this.bdpId,
		encourageSms = this.encourageSms,
		accountManagerId = this.accountManagerId,
		noteImportant = this.noteImportant,
		billDueDays = this.billDueDays,
		invoiceDueDays = this.invoiceDueDays,
		remittanceNotice = this.remittanceNotice,
		preferredMerchantId = this.preferredMerchantId,
		leadUserId = this.leadUserId,
		zendeskSellCompanyCode = this.zendeskSellCompanyCode,
		priceAdjustmentFactor = this.priceAdjustmentFactor,
		preferredMerchantBranchLocationId = this.preferredMerchantBranchLocationId,
		automaticPayment = this.automaticPayment,
		bankAccountNumber = this.bankAccountNumber,
		bankSortCode = this.bankSortCode,
		accountHolder = this.accountHolder,
		showRemittanceOnMobile = this.showRemittanceOnMobile,
		gasCertAvailable = this.gasCertAvailable,
		defaultUtilityId = this.defaultUtilityId,
		dealerPrices = this.dealerPrices,
		creditValueAirToWaterHp = this.creditValueAirToWaterHp,
		creditValueAirToAirHp = this.creditValueAirToAirHp,
		creditValueWaterToWaterHp = this.creditValueWaterToWaterHp,
		creditValueBrineToWaterHp = this.creditValueBrineToWaterHp,
		creditValueFlowsave = this.creditValueFlowsave,
		currency = this.currency,
		creditValueCapMode = this.creditValueCapMode,
		creditValueCap = this.creditValueCap,
		fleetFormId = this.fleetFormId,
		hasSignature = this.hasSignature
	}) {
		return new PartnerBrand(id, brandId, name, tradingName, labourCost, markup, domain, phoneNumber, logoId, siteId,
			vatRegistrationNumber, freeText, gasNumber, email, quoteEmailStyleSuffix, vatRegistered, bdpId, encourageSms,
			accountManagerId, noteImportant, this.noteImportantUpdatedDateTime, this.noteImportantAuthor, billDueDays,
			invoiceDueDays, remittanceNotice, preferredMerchantId, leadUserId, zendeskSellCompanyCode, priceAdjustmentFactor,
			preferredMerchantBranchLocationId, automaticPayment, bankAccountNumber, bankSortCode,
			accountHolder, showRemittanceOnMobile, gasCertAvailable, this.serviceReminderAvailable, defaultUtilityId,
			dealerPrices,
			creditValueAirToWaterHp, creditValueAirToAirHp, creditValueWaterToWaterHp, creditValueBrineToWaterHp,
			creditValueFlowsave, currency, creditValueCapMode, creditValueCap, fleetFormId, hasSignature);
	}

	toPartnerBrandSearch() {
		return new PartnerBrandSearch(this.id, this.name)
	}
}

export interface IPartnerBrandPaymentsStatus {
	outstandingBills: boolean
}

export class PartnerBrandSearch {
	constructor(public readonly id: string,
		public readonly name: string) {}

	static fromJson(json: any) {
		return new PartnerBrandSearch(json.id, json.name)
	}

	public static compare(a: PartnerBrandSearch, b: PartnerBrandSearch) {
		return a.name.toLowerCase().localeCompare(b.name.toLocaleLowerCase())
	}
}
